import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pagination } from "@/components/Pagination";

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => {
    api.get("/admin/reports").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  const safePage = Math.min(page, Math.max(1, Math.ceil(rows.length / pageSize)));
  const paged = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const exportCSV = () => {
    const headers = ["Name", "Email", "Batch", "Modules Completed", "Modules Total", "Progress", "Access Status", "Access Expiry"];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([r.name, r.email, r.batch || "", r.modules_completed, r.modules_total, `${r.progress || 0}%`, r.access_status, r.access_expiry].map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <AdminLayout title="Reports">
      <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Reports</h1>
          <p className="mt-1 text-muted-foreground">Student progress report.</p>
        </div>
        <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn"><Download className="h-4 w-4" /> Export CSV</Button>
      </div>
      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="table-wrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Batch</TableHead><TableHead>Modules</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead>Expiry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No data.</TableCell></TableRow>
                ) : paged.map((r) => (
                  <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.email}</TableCell>
                    <TableCell>{r.batch || "—"}</TableCell>
                    <TableCell>{r.modules_completed}/{r.modules_total}</TableCell>
                    <TableCell>{r.progress || 0}%</TableCell>
                    <TableCell><StatusBadge status={r.access_status} /></TableCell>
                    <TableCell>{r.access_expiry || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={safePage}
            pageSize={pageSize}
            total={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="reports-pagination"
          />
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
